import { PDFDocument, type PDFFont, type PDFImage, type PDFPage, rgb, StandardFonts } from "pdf-lib"

import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import { CLASE, plural, porOrigen, type Reporte, type Saldo } from "./tipos"

/**
 * El PDF del reporte de cuentas del 2.0, compuesto EN EL NAVEGADOR (12/10).
 *
 * ⚠ **No es la impresión del navegador, y es a propósito.** `window.print()` no hace
 * nada en buena parte de los móviles —el navegador de Telegram, entre ellos—, que es
 * justo desde donde se abre este enlace. Aquí se arma el archivo con `pdf-lib` y se
 * descarga: funciona igual en el teléfono y en el escritorio.
 *
 * ⚠ **Las páginas se llenan; no se deja una hoja en blanco por no partir a un cliente.**
 * La tabla sigue en la página siguiente repitiendo su cabecera y el nombre con
 * «(continúa)». Lo único que no se separa es el nombre de su primera fila.
 */

// ⚠ Lo exportado lo reutiliza el PDF de la semana (`pdfSemana.ts`, 13/10): el mismo
// papel, la misma tinta y el mismo membrete, sin una segunda copia que se separe.
export const ANCHO = 792 // carta apaisada, en puntos
export const ALTO = 612
export const MARGEN = 36
export const TINTA = rgb(0.14, 0.06, 0.1)
export const GRIS = rgb(0.42, 0.33, 0.38)
export const LINEA = rgb(0.85, 0.82, 0.84)
export const VINO = rgb(0.478, 0.106, 0.22)
export const ROJO = rgb(0.7, 0.13, 0.13)

/** Columnas de la tabla: rótulo, ancho y alineación. Concepto se lleva lo que sobra. */
const COLUMNAS: { rotulo: string; ancho: number; derecha?: boolean }[] = [
  { rotulo: "Expediente", ancho: 70 },
  { rotulo: "Concepto", ancho: 0 },
  { rotulo: "Emisión", ancho: 62 },
  { rotulo: "Vence", ancho: 62 },
  { rotulo: "Días", ancho: 34, derecha: true },
  { rotulo: "Monto", ancho: 84, derecha: true },
  { rotulo: "Abonado", ancho: 70, derecha: true },
  { rotulo: "Saldo", ancho: 84, derecha: true },
]
export const UTIL = ANCHO - 2 * MARGEN
const CONCEPTO = UTIL - COLUMNAS.reduce((s, c) => s + c.ancho, 0) - 8 * 6
const ANCHOS = COLUMNAS.map((c) => (c.rotulo === "Concepto" ? CONCEPTO : c.ancho))

/** Lo que Helvetica no codifica se cambia por «?»: un carácter raro no tumba el PDF. */
export function escribible(fuente: PDFFont, texto: string): string {
  let salida = ""
  for (const letra of texto) {
    try {
      fuente.encodeText(letra)
      salida += letra
    } catch {
      salida += "?"
    }
  }
  return salida
}

/** El texto partido por palabras para que quepa en `ancho`. */
export function lineas(fuente: PDFFont, cuerpo: number, texto: string, ancho: number): string[] {
  const salida: string[] = []
  let actual = ""
  for (const palabra of escribible(fuente, texto).split(/\s+/).filter(Boolean)) {
    const prueba = actual ? `${actual} ${palabra}` : palabra
    if (fuente.widthOfTextAtSize(prueba, cuerpo) <= ancho || actual === "") actual = prueba
    else {
      salida.push(actual)
      actual = palabra
    }
  }
  if (actual) salida.push(actual)
  return salida.length ? salida : [""]
}

export const enMonedas = (lista: Saldo[]) =>
  lista.length === 0 ? "—" : lista.map((s) => `${s.moneda} ${monto(s.saldo)}`).join(" + ")

export type Lienzo = {
  pdf: PDFDocument
  pagina: PDFPage
  y: number
  normal: PDFFont
  negrita: PDFFont
}

export function texto(
  l: Lienzo,
  t: string,
  x: number,
  y: number,
  o: { cuerpo?: number; fuente?: PDFFont; color?: ReturnType<typeof rgb>; derecha?: number } = {},
) {
  const fuente = o.fuente ?? l.normal
  const cuerpo = o.cuerpo ?? 8.5
  const limpio = escribible(fuente, t)
  const px = o.derecha !== undefined ? x + o.derecha - fuente.widthOfTextAtSize(limpio, cuerpo) : x
  l.pagina.drawText(limpio, { x: px, y, size: cuerpo, font: fuente, color: o.color ?? TINTA })
}

export function nuevaPagina(l: Lienzo) {
  l.pagina = l.pdf.addPage([ANCHO, ALTO])
  l.y = ALTO - MARGEN
}

function cabeceraDeTabla(l: Lienzo) {
  let x = MARGEN
  COLUMNAS.forEach((c, i) => {
    texto(l, c.rotulo, x, l.y, {
      cuerpo: 7.5,
      fuente: l.negrita,
      color: GRIS,
      derecha: c.derecha ? ANCHOS[i] : undefined,
    })
    x += (ANCHOS[i] ?? 0) + 6
  })
  l.y -= 5
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: ANCHO - MARGEN, y: l.y },
    thickness: 0.5,
    color: LINEA,
  })
  l.y -= 11
}

export function membrete(
  l: Lienzo,
  r: Pick<Reporte, "empresa_nombre" | "empresa_rif">,
  logo: PDFImage | null,
) {
  const alto = 42
  if (logo) {
    const ancho = (logo.width / logo.height) * alto
    l.pagina.drawImage(logo, { x: MARGEN, y: l.y - alto, width: ancho, height: alto })
  }
  texto(l, r.empresa_nombre.toUpperCase(), MARGEN, l.y - 14, {
    cuerpo: 12,
    fuente: l.negrita,
    derecha: UTIL,
  })
  if (r.empresa_rif)
    texto(l, `RIF ${r.empresa_rif}`, MARGEN, l.y - 28, { cuerpo: 9, color: GRIS, derecha: UTIL })
  l.y -= alto + 8
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: ANCHO - MARGEN, y: l.y },
    thickness: 1.2,
    color: VINO,
  })
  l.y -= 22
}

/** Compone el PDF. `logo` son los bytes de un PNG; sin él, el membrete va sin marca. */
export async function pdfDelReporte(
  r: Reporte,
  logo: Uint8Array | null,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(`${r.titulo} abiertas · ${comoFecha(r.fecha)}`)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([ANCHO, ALTO]),
    y: ALTO - MARGEN,
    normal: await pdf.embedFont(StandardFonts.Helvetica),
    negrita: await pdf.embedFont(StandardFonts.HelveticaBold),
  }
  const imagen = logo ? await pdf.embedPng(logo) : null

  membrete(l, r, imagen)
  texto(l, `${r.titulo} abiertas`, MARGEN, l.y, { cuerpo: 16, fuente: l.negrita })
  l.y -= 15
  texto(
    l,
    `Reporte del ${comoFecha(r.fecha)}, generado el ${comoFechaHora(r.generado_en)}`,
    MARGEN,
    l.y,
    {
      cuerpo: 8.5,
      color: GRIS,
    },
  )
  l.y -= 20
  const resumen = [
    `Cuentas abiertas: ${r.total_cuentas} de ${r.grupos.length} ${plural(r.rotulo_tercero)}`,
    `Saldo: ${enMonedas(r.saldos)}`,
    `Vencidas: ${r.vencidas}${r.vencidas > 0 ? ` · ${enMonedas(r.saldos_vencidos)}` : ""}`,
  ]
  texto(l, resumen.join("     "), MARGEN, l.y, { cuerpo: 9.5, fuente: l.negrita })
  l.y -= 15
  texto(
    l,
    `Por antigüedad:  ${r.tramos.map((t) => `${t.rotulo}: ${enMonedas(t.saldos)}`).join("   ·   ")}`,
    MARGEN,
    l.y,
    {
      cuerpo: 8,
      color: GRIS,
    },
  )
  const origenes = porOrigen(r)
  if (origenes.length > 0) {
    l.y -= 12
    texto(
      l,
      `Por origen:  ${origenes.map((o) => `${o.rotulo}: ${enMonedas(o.saldos)}`).join("   ·   ")}`,
      MARGEN,
      l.y,
      { cuerpo: 8, color: GRIS },
    )
  }
  l.y -= 26

  if (r.grupos.length === 0) texto(l, "No había cuentas abiertas.", MARGEN, l.y, { color: GRIS })

  for (const g of r.grupos) {
    // El nombre no se queda solo al pie: con él van la cabecera y al menos una fila.
    if (l.y < MARGEN + 70) nuevaPagina(l)
    const encabezar = (sigue: boolean) => {
      texto(l, `${g.tercero}${sigue ? " (continúa)" : ""}`, MARGEN, l.y, {
        cuerpo: 10.5,
        fuente: l.negrita,
      })
      if (g.rif) {
        const ancho = l.negrita.widthOfTextAtSize(
          escribible(l.negrita, g.tercero + (sigue ? " (continúa)" : "")),
          10.5,
        )
        texto(l, g.rif, MARGEN + ancho + 8, l.y, { cuerpo: 8, color: GRIS })
      }
      texto(l, enMonedas(g.saldos), MARGEN, l.y, { cuerpo: 10.5, fuente: l.negrita, derecha: UTIL })
      l.y -= 16
      cabeceraDeTabla(l)
    }
    encabezar(false)
    for (const c of g.cuentas) {
      const concepto = lineas(l.normal, 8.5, c.concepto, CONCEPTO)
      const alto = concepto.length * 10.5 + 10 + 6
      if (l.y - alto < MARGEN + 14) {
        nuevaPagina(l)
        encabezar(true)
      }
      const celdas = [
        c.expediente || "—",
        "",
        comoFecha(c.emision) || "—",
        comoFecha(c.vence) || "—",
        String(c.antiguedad),
        `${c.moneda} ${monto(c.monto)}`,
        monto(c.abonado),
        `${c.moneda} ${monto(c.saldo)}`,
      ]
      let x = MARGEN
      COLUMNAS.forEach((col, i) => {
        if (col.rotulo === "Concepto") {
          concepto.forEach((ln, k) => {
            texto(l, ln, x, l.y - k * 10.5)
          })
          texto(l, CLASE[c.clase] ?? c.clase, x, l.y - concepto.length * 10.5, {
            cuerpo: 7,
            color: GRIS,
          })
        } else {
          texto(l, celdas[i] ?? "", x, l.y, {
            fuente: col.rotulo === "Saldo" ? l.negrita : l.normal,
            derecha: col.derecha ? ANCHOS[i] : undefined,
          })
          if (col.rotulo === "Vence" && c.vencida)
            texto(l, "Vencida", x, l.y - 10.5, { cuerpo: 7, fuente: l.negrita, color: ROJO })
        }
        x += (ANCHOS[i] ?? 0) + 6
      })
      l.y -= alto - 4
      l.pagina.drawLine({
        start: { x: MARGEN, y: l.y + 8 },
        end: { x: ANCHO - MARGEN, y: l.y + 8 },
        thickness: 0.4,
        color: LINEA,
      })
    }
    l.y -= 14
  }

  const paginas = pdf.getPages()
  paginas.forEach((p, i) => {
    const pie = escribible(
      l.normal,
      `${r.empresa_nombre} · ${r.titulo} · página ${i + 1} de ${paginas.length}`,
    )
    p.drawText(pie, {
      x: ANCHO - MARGEN - l.normal.widthOfTextAtSize(pie, 7),
      y: MARGEN / 2,
      size: 7,
      font: l.normal,
      color: GRIS,
    })
  })

  return new Uint8Array(await pdf.save())
}

/**
 * El logo vectorial de la página, como PNG para el PDF. `null` si el navegador no puede
 * rasterizarlo (o en las pruebas, sin lienzo): el PDF sale igual, sin marca.
 */
export async function logoComoPng(
  svg: SVGSVGElement | null,
  color: string,
): Promise<Uint8Array | null> {
  try {
    if (!svg) return null
    const copia = svg.cloneNode(true) as SVGSVGElement
    copia.setAttribute("style", `color: ${color}`)
    const caja = svg.viewBox.baseVal
    const ancho = 600
    const alto = Math.round((ancho * caja.height) / caja.width)
    copia.setAttribute("width", String(ancho))
    copia.setAttribute("height", String(alto))
    const imagen = new Image()
    imagen.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copia))}`
    await imagen.decode()
    const lienzo = document.createElement("canvas")
    lienzo.width = ancho
    lienzo.height = alto
    const ctx = lienzo.getContext("2d")
    if (!ctx) return null
    ctx.drawImage(imagen, 0, 0, ancho, alto)
    const blob = await new Promise<Blob | null>((ok) => lienzo.toBlob(ok, "image/png"))
    return blob ? new Uint8Array(await blob.arrayBuffer()) : null
  } catch {
    return null
  }
}

/** Descarga los bytes como archivo. En el teléfono abre el visor o la descarga. */
export function descargar(bytes: Uint8Array<ArrayBuffer>, nombre: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }))
  const a = document.createElement("a")
  a.href = url
  a.download = nombre
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
